import { useState, useEffect, useRef, useCallback } from 'react'
import { getApiUrl, fetchWithAuth } from '../../api'
import { useNavigate } from 'react-router-dom'

interface NotificationItem {
  id: number
  title: string
  content: string
  ref_type: string | null
  ref_id: number | null
  is_read: number
  created_at: string
}

const ITEM_HEIGHT = 72 // 알림 1개 높이 (px 기준)
const PAGE_SIZE = 20

const REF_TYPE_TO_PATH: Record<string, string> = {
  program_application: '/admin/program',
  counseling_application: '/admin/counsel',
  space_reservation: '/admin/space',
  locker_reservation: '/admin/locker',
}

export default function AdminNotificationBell() {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const sseRef = useRef<EventSource | null>(null)
  const initialLimitRef = useRef(6)

  useEffect(() => {
    if (Notification.permission === 'default') {
        Notification.requestPermission()
    }
  }, [])

  const fetchInitial = useCallback(async () => {
    try {
      // 미읽음 개수 먼저 확인
      const countRes = await fetchWithAuth(getApiUrl('/marubit/api/notifications?limit=1'))
      if (!countRes.ok) return
      const countData = await countRes.json()
      const unread = countData.unread_count as number

      // 초기 limit = max(6, 미읽음 수)
      const limit = Math.max(6, unread)
      initialLimitRef.current = limit

      const res = await fetchWithAuth(getApiUrl(`/marubit/api/notifications?limit=${limit}&skip=0`))
      if (!res.ok) return
      const data = await res.json()
      setNotifications(data.notifications)
      setUnreadCount(data.unread_count)
      setHasMore(data.total > limit)
      setPage(1)
    } catch {}
  }, [])

  const fetchMore = useCallback(async () => {
    if (loading || !hasMore) return
    setLoading(true)
    try {
      const skip = initialLimitRef.current + (page - 1) * PAGE_SIZE
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/notifications?limit=${PAGE_SIZE}&skip=${skip}`)
      )
      if (!res.ok) return
      const data = await res.json()
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n.id))
        const newItems = data.notifications.filter((n: NotificationItem) => !existingIds.has(n.id))
        return [...prev, ...newItems]
      })
      setHasMore(data.total > skip + PAGE_SIZE)
      setPage(prev => prev + 1)
    } catch {}
    finally { setLoading(false) }
  }, [loading, hasMore, page])

  useEffect(() => {
    fetchInitial()

    const token = localStorage.getItem('access_token')
    if (!token) return

    const sse = new EventSource(getApiUrl(`/api/events/marubit-notifications`))
    sseRef.current = sse

    sse.onmessage = (e) => {
        try {
            const data = JSON.parse(e.data)
            if (data.type === 'marubit_notification') {
            fetchInitial()
            if (Notification.permission === 'granted') {
                new Notification(data.title ?? '새 알림', {
                body: data.content ?? '',
                icon: '/favicon.ico',
                })
            }
            }
        } catch {}
    }

    return () => { sse.close() }
  }, [fetchInitial])

  // 외부 클릭 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 무한스크롤
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const handleScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        fetchMore()
      }
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [fetchMore])

  const handleMarkRead = async (id: number, ref_type: string | null) => {
    try {
        await fetchWithAuth(getApiUrl(`/marubit/api/notifications/${id}/read`), { method: 'PATCH' })
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n))
        setUnreadCount(prev => Math.max(0, prev - 1))
        if (ref_type && REF_TYPE_TO_PATH[ref_type]) {
        navigate(REF_TYPE_TO_PATH[ref_type])
        setShowDropdown(false)
        }
    } catch {}
  }

  const handleMarkAllRead = async () => {
    try {
      await fetchWithAuth(getApiUrl('/marubit/api/notifications/read-all'), { method: 'PATCH' })
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })))
      setUnreadCount(0)
    } catch {}
  }

  const formatTime = (dateStr: string) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
    if (diff < 60) return '방금 전'
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`
    return `${Math.floor(diff / 86400)}일 전`
  }

  // 6개 높이 고정
  const listMaxHeight = ITEM_HEIGHT * 6

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 종 버튼 */}
      <button
        onClick={() => setShowDropdown(prev => !prev)}
        className="relative w-8 h-8 flex items-center justify-center rounded-lg bg-transparent border-none cursor-pointer hover:bg-white/10 transition-colors"
      >
        <span className="text-[18px]">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-[3px]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* 드롭다운 */}
      {showDropdown && (
        <div
            className="absolute left-0 top-10 bg-white rounded-xl shadow-2xl border border-gray-100"
            style={{ width: '300px', zIndex: 9999 }}
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-[14px] font-semibold text-gray-800">알림</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-[12px] text-[#887267] bg-transparent border-none cursor-pointer hover:text-[#6b5248]"
              >
                전체 읽음
              </button>
            )}
          </div>

          {/* 알림 목록 */}
          <div
            ref={listRef}
            className="overflow-y-auto rounded-b-xl"
            style={{ maxHeight: `${listMaxHeight}px` }}
          >
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-[13px] text-gray-400">
                알림이 없습니다
              </div>
            ) : (
              <>
                {notifications.map(n => (
                  <div
                    key={n.id}
                    onClick={() => handleMarkRead(n.id, n.ref_type)}
                    className={[
                      'px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors rounded-none',
                      n.is_read === 0
                        ? 'bg-[#fffbf5] hover:bg-[#fff5e6]'
                        : 'hover:bg-gray-50',
                    ].join(' ')}
                    style={{ minHeight: `${ITEM_HEIGHT}px` }}
                  >
                    <div className="flex items-start gap-2">
                      {n.is_read === 0 && (
                        <span className="w-2 h-2 mt-[5px] rounded-full bg-red-500 shrink-0" />
                      )}
                      <div className={n.is_read === 0 ? '' : 'ml-4'}>
                        <p className="text-[13px] font-semibold text-gray-800 m-0">{n.title}</p>
                        <p className="text-[12px] text-gray-500 m-0 mt-[2px] leading-snug">{n.content}</p>
                        <p className="text-[11px] text-gray-400 m-0 mt-[4px]">{formatTime(n.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="py-3 text-center text-[12px] text-gray-400">불러오는 중...</div>
                )}
                {!hasMore && notifications.length > 0 && (
                  <div className="py-3 text-center text-[12px] text-gray-300">마지막 알림입니다</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}